import type { JSX, ReactNode } from "react";

export interface EntityRowProps {
  /** Circular entity mark (brand logo, market flag). Mutually exclusive with `glyph`. */
  mark?: ReactNode;
  /** Stroked glyph for kinds that have no entity image (filters, segments). */
  glyph?: ReactNode;
  name: string;
  /** Right rail. Supply only in sections that hold more than one kind or role. */
  qualifier?: ReactNode;
  /** Caution surface: an out-of-reach reference, rendered on the amber ground. */
  tone?: "default" | "caution";
  /** Hairline above the row. Off for short lists that a heading already bounds. */
  divider?: boolean;
}

/**
 * One entity, one line. Shared by the saved-view detail panel's lists and the
 * limited-access modal's ledger so the two surfaces cannot drift: a brand reads the
 * same whether it is part of the view or missing from it.
 *
 * The name is the only element allowed to truncate; the qualifier holds its width so
 * the right rail stays a readable column down a long list.
 */
const EntityRow = ({
  mark,
  glyph,
  name,
  qualifier,
  tone = "default",
  divider = true,
}: EntityRowProps): JSX.Element => (
  <div
    className={`flex items-center gap-[9px] ${
      divider
        ? `py-[6px] border-t first:border-t-0 ${
            tone === "caution" ? "border-caution-border" : "border-neutral-200"
          }`
        : "py-[3px]"
    }`}
  >
    {mark ? (
      <span className={`shrink-0 ${tone === "caution" ? "opacity-40 grayscale" : ""}`}>{mark}</span>
    ) : null}
    {glyph ? (
      <span
        className={`w-[22px] shrink-0 flex justify-center ${
          tone === "caution" ? "text-caution-500" : "text-neutral-500"
        }`}
      >
        {glyph}
      </span>
    ) : null}
    {/* The name stays ink on a caution row. Amber is reserved vocabulary and belongs to the
        status that earns it; spreading it across the names makes a list of ordinary brands
        read as a page of warnings. */}
    <span className="flex-1 min-w-0 truncate text-[12px] font-medium text-text-300">
      {name}
    </span>
    {qualifier ? (
      <span
        className={`shrink-0 whitespace-nowrap text-[11px] ${
          tone === "caution" ? "font-semibold text-caution-500" : "text-neutral-500"
        }`}
      >
        {qualifier}
      </span>
    ) : null}
  </div>
);

export default EntityRow;
